import { useI18n } from 'hooks/useI18n';
import { UIOfferRanges } from 'interfaces/ui/ui-offer-when-schedule';
import {
  StyledWhenInfographic,
  StyledWhenInfographicDuration,
  StyledWhenInfographicDurationItem,
  StyledWhenInfographicHourInfo,
  StyledWhenInfographicHourItem,
  StyledWhenInfographicHours,
  StyledWhenInfographicHourSeparator,
  StyledWhenInfographicHoursSection,
  StyledWhenInfographicLabelItem,
  StyledWhenInfographicLabelItemExtra,
  StyledWhenInfographicLabels
} from './WhenInfographic.styled';

type WhenInfographicProps = {
  ranges: UIOfferRanges[];
  title: string;
  subtitle?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inOutFlexibleMinutes?: number | any;
  caption: string;
};
const WhenInfographic = ({
  ranges,
  title,
  subtitle,
  inOutFlexibleMinutes,
  caption
}: WhenInfographicProps) => {
  const { t } = useI18n('job-offer');

  let totalHours = 0;

  ranges.forEach((range: UIOfferRanges) => {
    return (totalHours = totalHours + Math.ceil(range.totalTime));
  });

  let flexibleHours = 0;
  ranges.forEach((range: UIOfferRanges) => {
    if (range.flexibleTime) {
      flexibleHours = flexibleHours + range.totalTime;
    }
  });

  const fixedHours = totalHours - Math.ceil(flexibleHours / 2) * 2;

  function getSchedule(
    flexible: boolean | undefined,
    free: boolean | undefined
  ): string {
    if (flexible) {
      return 'flexible';
    } else if (free) {
      return 'free';
    } else {
      return '';
    }
  }

  const totalHoursVar = {
    '--total-hours': totalHours,
    '--flexible-hours': Math.ceil(flexibleHours / 2),
    '--fixed-hours': fixedHours
  };

  const Hours: React.FC<{
    hours: number;
  }> = ({ hours }) => {
    return (
      <>
        {hours < 1 && (
          <>
            {hours * 60} {t('when.minutes')}
          </>
        )}

        {hours >= 1 && (
          <>
            {hours} {t('when.hours')}
          </>
        )}
      </>
    );
  };

  return (
    <StyledWhenInfographic style={totalHoursVar}>
      <div aria-hidden="true">
        <StyledWhenInfographicLabels>
          {inOutFlexibleMinutes > 0 && (
            <StyledWhenInfographicLabelItem
              schedule="flexible"
              className="flex-in"
            >
              {t('when.schedules.flexIn')}
            </StyledWhenInfographicLabelItem>
          )}
          <StyledWhenInfographicLabelItem schedule="fixed">
            <span className="fixed">{title}</span>
            {inOutFlexibleMinutes > 0 && <StyledWhenInfographicHourSeparator />}
            {subtitle && (
              <StyledWhenInfographicLabelItemExtra>
                {subtitle}
              </StyledWhenInfographicLabelItemExtra>
            )}
          </StyledWhenInfographicLabelItem>

          {inOutFlexibleMinutes > 0 && (
            <StyledWhenInfographicLabelItem
              schedule="flexible"
              className="flex-out"
            >
              {t('when.schedules.flexOut')}
            </StyledWhenInfographicLabelItem>
          )}
        </StyledWhenInfographicLabels>
        <StyledWhenInfographicDuration>
          {ranges &&
            ranges.map((range: UIOfferRanges, i: number) => (
              <StyledWhenInfographicDurationItem
                key={i}
                schedule={getSchedule(range.flexibleTime, range.freeTime)}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                style={{ ['--time-span' as any]: Math.ceil(range.totalTime) }}
                className={`${range.freeTime ? 'is-free' : ''}`}
              >
                <Hours hours={range.totalTime} />
              </StyledWhenInfographicDurationItem>
            ))}
        </StyledWhenInfographicDuration>
        <StyledWhenInfographicHours>
          {ranges &&
            ranges.map((range: UIOfferRanges, i: number) => (
              <StyledWhenInfographicHoursSection
                key={i}
                schedule={getSchedule(range.flexibleTime, range.freeTime)}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                style={{ ['--time-span' as any]: Math.ceil(range.totalTime) }}
              >
                {[...Array(Math.ceil(range.totalTime))].map((h, i) => (
                  <StyledWhenInfographicHourItem key={i} />
                ))}
                {i === 0 && range.flexibleTime && (
                  <StyledWhenInfographicHourInfo className="flex-in">
                    <span>{range.startTime}</span>
                    <StyledWhenInfographicHourSeparator />
                  </StyledWhenInfographicHourInfo>
                )}
                {!range.freeTime &&
                  ((range.flexibleTime && i > 1) || !range.flexibleTime) &&
                  ((range.flexibleTime && i < ranges.length - 1) ||
                    !range.flexibleTime) && (
                    <StyledWhenInfographicHourInfo className="fixed">
                      <span className="fixed-in">{range.startTime}</span>
                      <StyledWhenInfographicHourSeparator />
                      <span className="fixed-out">{range.endTime}</span>
                    </StyledWhenInfographicHourInfo>
                  )}
                {range.freeTime && (
                  <StyledWhenInfographicHourInfo className="is-free">
                    <span>({t('when.schedules.freeTime')})</span>
                  </StyledWhenInfographicHourInfo>
                )}
                {i === ranges.length - 1 && range.flexibleTime && (
                  <StyledWhenInfographicHourInfo className="flex-out">
                    <StyledWhenInfographicHourSeparator />
                    <span>{range.endTime}</span>
                  </StyledWhenInfographicHourInfo>
                )}
              </StyledWhenInfographicHoursSection>
            ))}
        </StyledWhenInfographicHours>
      </div>
      <figcaption className="sr-only">{caption}</figcaption>
    </StyledWhenInfographic>
  );
};

export default WhenInfographic;
